<script>
import toast from '@services/toasts/index.js'
import baseMixin from '@/mixins/baseMixin'
import IncludeCheckboxes from '@/components/booking/bookingList/export/include-checkboxes.vue'
import ExportWhat from '@/components/booking/bookingList/export/export-what.vue'
import ExportColumns from './export/export-columns.vue'
import ExportDateRange from './export/export-date-range.vue'

export default {
  name: 'ExportBookings',
  components: {
    ExportDateRange,
    IncludeCheckboxes,
    ExportWhat,
    ExportColumns,
  },
  mixins: [baseMixin],
  props: {
    exportMode: { type: String, default: 'all' },
    selectedCount: { type: Number, default: 0 },
    selectedBookings: { type: Array, default: () => [] },
    filters: { type: Object, required: true, default: () => ({}) },
  },
  emits: ['unselected-rows'],
  data() {
    return {
      isExporting: false,
      customDateRange: [this.filters?.startDate, this.filters?.endDate],
      selectedRange: 'custom',
      showDropdown: false,
      condition: this.filters?.dateFilterCondition ?? 'is',
      dropdownTriggerEl: null,
      isDateRangePicker: false,
      dropdownCoords: { top: 0, left: 0 },
      include: {
        active: ['all', 'upcoming', 'past'].includes(this.filters?.tabType),
        cancelled: this.filters?.tabType === 'cancelled',
        unapproved: this.filters?.tabType === 'ApprovalRequired',
      },
      exportType: 'filtered',
      selectedColumns: {},
    }
  },
  computed: {
    description() {
      if (this.exportMode !== 'selected') {
        return this.$t('exportBookings.description.default')
      } else {
        return this.$t('exportBookings.description.selected', {
          count: this.selectedCount,
        })
      }
    },
  },
  methods: {
    formatISO(d) {
      const pad = (n) => String(n).padStart(2, '0')
      const yyyy = d.getFullYear()
      const mm = pad(d.getMonth() + 1)
      const dd = pad(d.getDate())
      return `${yyyy}-${mm}-${dd}`
    },
    offsetDays(days) {
      const d = new Date()
      d.setHours(0, 0, 0, 0)
      d.setDate(d.getDate() + days)
      return d
    },
    computeStartEnd() {
      let startdate = null
      let enddate = null

      if (this.exportMode === 'selected') return { startdate, enddate }
      if (this.selectedRange === 'last7') {
        enddate = this.formatISO(this.offsetDays(0))
        startdate = this.formatISO(this.offsetDays(-6))
        return { startdate, enddate }
      }
      if (this.selectedRange === 'last30') {
        enddate = this.formatISO(this.offsetDays(0))
        startdate = this.formatISO(this.offsetDays(-29))
        return { startdate, enddate }
      }

      if (this.selectedRange === 'custom') {
        const [first, second] = this.customDateRange || []

        if (this.condition === 'is') {
          if (first) {
            const d = this.formatISO(new Date(first))
            startdate = d
            enddate = d
          }
        } else if (this.condition === 'is before') {
          if (first) {
            enddate = this.formatISO(new Date(first))
            startdate = ''
          }
        } else if (this.condition === 'is after') {
          if (first) {
            startdate = this.formatISO(new Date(first))
            enddate = ''
          }
        } else if (this.condition === 'is between') {
          if (first) startdate = this.formatISO(new Date(first))
          if (second) enddate = this.formatISO(new Date(second))
        }
      }
      return { startdate, enddate }
    },
    async getExportData() {
      this.isExporting = true
      try {
        const selectedColumns = Object.entries(this.selectedColumns)
          .filter(([_, value]) => value)
          .map(([key]) => key)

        let startdate = null
        let enddate = null
        let bookingIds = null

        if (this.exportMode === 'selected') {
          const dateObjs = (this.selectedBookings || [])
            .map((b) => b?.startDateTimeLocal)
            .filter(Boolean)
            .map((s) => new Date(s))

          if (dateObjs.length) {
            const minDate = new Date(Math.min(...dateObjs))
            const maxDate = new Date(Math.max(...dateObjs))
            startdate = this.formatYMD(minDate)
            enddate = this.formatYMD(maxDate)
          } else {
            startdate = null
            enddate = null
          }

          bookingIds = (this.selectedBookings || []).map((b) => b.id)
        } else {
          const range = this.computeStartEnd()
          startdate = range.startdate
          enddate = range.enddate
        }
        if (this.exportMode === 'selected' && !this.selectedCount) {
          const range = this.computeStartEnd()
          startdate = this.filters?.startDate ?? range.startdate
          enddate = this.filters?.endDate ?? range.startdate
        }

        let payload = {
          columns: selectedColumns,
          startdate,
          enddate,
          bookingIds,
          IsBlockBooking: this.filters?.isBlockBooking ?? false,
          tabType: this.filters?.tabType ?? 0,
          isDescending: this.filters?.isDescending ?? false,
          ...(this.filters.sortColumn
            ? { sortColumn: this.filters.sortColumn }
            : {}),
        }

        if (this.exportType !== 'all') {
          let status = this.filters.status?.length
            ? [...this.filters.status]
            : []

          // Special cases: only cancelled or only unapproved selected
          if (
            this.include.cancelled &&
            !this.include.active &&
            !this.include.unapproved
          ) {
            status = ['cancelled']
          } else if (
            this.include.unapproved &&
            !this.include.active &&
            !this.include.cancelled
          ) {
            status = ['open']
          } else {
            // Rule 1: remove 'cancelled' if active is true
            if (
              this.include.active &&
              (!this.filters.status || !this.filters.status.length)
            ) {
              status.push('open', 'confirmed', 'pending')
            }
            if (this.include.active) {
              status = status.filter((s) => s !== 'cancelled')
            }
            // Rule 2: add 'cancelled' if include.cancelled is true
            if (this.include.cancelled && !status.includes('cancelled')) {
              status.push('cancelled')
            }
            // Rule 3: add 'open' if include.unapproved is true
            if (this.include.unapproved && !status.includes('open')) {
              status.push('open')
            }
            status = [...new Set(status)]
          }
          payload = {
            ...payload,
            ...(this.filters.clientids?.length
              ? { clientids: this.filters.clientids }
              : {}),
            status: status,
            ...(this.filters.educators?.length
              ? { educators: this.filters.educators }
              : {}),
            ...(this.filters.classifications?.length
              ? { classifications: this.filters.classifications }
              : {}),
            ...(this.filters.certifications?.length
              ? { certifications: this.filters.certifications }
              : {}),
            ...(this.filters.grades?.length
              ? { grades: this.filters.grades }
              : {}),
            ...(this.filters.replacings?.length
              ? { replacings: this.filters.replacings }
              : {}),
            ...(this.filters.locations?.length
              ? { locations: this.filters.locations }
              : {}),
          }
        } else {
          let status = []

          // Special cases: only cancelled or only unapproved selected
          if (
            this.include.cancelled &&
            !this.include.active &&
            !this.include.unapproved
          ) {
            status = ['cancelled']
          } else if (
            this.include.unapproved &&
            !this.include.active &&
            !this.include.cancelled
          ) {
            status = ['open']
          } else {
            // Rule 1: remove 'cancelled' if active is true
            if (this.include.active) {
              status.push('open', 'confirmed', 'pending')
              status = status.filter((s) => s !== 'cancelled')
            }
            // Rule 2: add 'cancelled' if include.cancelled is true
            if (this.include.cancelled && !status.includes('cancelled')) {
              status.push('cancelled')
            }
            // Rule 3: add 'open' if include.unapproved is true
            if (this.include.unapproved && !status.includes('open')) {
              status.push('open')
            }
            status = [...new Set(status)]
          }
          payload = {
            ...payload,
            status: status,
          }
        }
        if (this.exportMode === 'selected') {
          if (!this.selectedCount) {
            payload = {
              ...payload,
            }
          } else {
            const { status, ...rest } = payload
            payload = { ...rest }
          }
        }

        // --- Add timeout wrapper
        let timer
        const timeout = new Promise((resolve, reject) => {
          timer = setTimeout(() => {
            this.$emit('close')
            this.$emit('unselected-rows')
            const toastErrorMessage = `Export request timed out after 60000ms`
            toast.error(toastErrorMessage)
            reject(new Error(toastErrorMessage))
          }, 60000)
        })

        const res = await Promise.race([
          this.$store.dispatch('bookings/getExportData', payload),
          timeout,
        ])

        // cancel timeout if export finished first
        clearTimeout(timer)

        const blob = await this.normalizeToBlob(res)
        if (!blob || !blob.size) {
          console.error('Empty export blob')
          return
        }
        this.downloadBlob(blob, this.suggestFileName())
        await this.$nextTick()
        setTimeout(() => {
          this.$emit('close')
          this.$emit('unselected-rows')
          this.isExporting = false
        }, 0)
      } catch (e) {
        console.error('Export failed:', e)
        this.$emit('close')
        this.$emit('unselected-rows')
        this.isExporting = false
      } finally {
        if (this.isExporting) {
          this.$emit('close')
          this.$emit('unselected-rows')
          this.isExporting = false
        }
      }
    },

    async normalizeToBlob(result) {
      if (
        result &&
        typeof result === 'object' &&
        typeof result.blob === 'function' &&
        'body' in result
      ) {
        return await result.blob()
      }

      if (result instanceof Blob) return result
      if (result && result.data instanceof Blob) return result.data

      if (result instanceof ArrayBuffer)
        return new Blob([result], { type: 'text/csv;charset=utf-8' })
      if (result && result.data instanceof ArrayBuffer) {
        return new Blob([result.data], { type: 'text/csv;charset=utf-8' })
      }

      if (typeof result === 'string') return this.stringToCsvBlob(result)
      if (result && typeof result.data === 'string')
        return this.stringToCsvBlob(result.data)
      if (result && typeof result.payload === 'string')
        return this.stringToCsvBlob(result.payload)

      if (this.isReadableStream(result)) {
        const text = await this.readStreamToText(result)
        return this.stringToCsvBlob(text)
      }

      console.warn('Unknown export result shape:', {
        type: typeof result,
        ctor: result?.constructor?.name,
        keys: result ? Object.keys(result) : null,
      })
      return new Blob([], { type: 'text/csv;charset=utf-8' })
    },
    isReadableStream(obj) {
      return obj && typeof obj.getReader === 'function'
    },

    async readStreamToText(stream) {
      const reader = stream.getReader()
      const decoder = new TextDecoder('utf-8')
      let chunks = ''
      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        chunks += decoder.decode(value, { stream: true })
      }
      chunks += decoder.decode()
      return chunks
    },

    suggestFileName() {
      const pad = (n) => String(n).padStart(2, '0')
      const d = new Date()
      const yyyy = d.getFullYear()
      const mm = pad(d.getMonth() + 1)
      const dd = pad(d.getDate())
      const stamp = `${yyyy}-${mm}-${dd}`

      return this.exportMode === 'selected'
        ? `bookings_selected_${stamp}.csv`
        : `bookings_${this.selectedRange}_${stamp}.csv`
    },

    stringToCsvBlob(csvText) {
      const BOM = '\uFEFF'
      return new Blob([BOM + csvText], { type: 'text/csv;charset=utf-8' })
    },

    downloadBlob(blob, filename) {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.setAttribute('download', filename)
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
    formatYMD(d) {
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    },
  },
}
</script>

<template>
  <div
    class="export-container"
    style="align-items: center; justify-content: center"
  >
    <div class="export-bookings">
      <div class="content">
        <h4 class="title">{{ $t('exportBookings.title') }}</h4>
        <p class="description">{{ description }}<span>Excel file</span></p>

        <!-- Date Range -->
        <template v-if="exportMode !== 'selected'">
          <label class="section-label">{{
            $t('exportBookings.dateRange.label')
          }}</label>
          <ExportDateRange
            :selected-range="selectedRange"
            :custom-date-range="customDateRange"
            :condition="condition"
            @update:selectedRange="selectedRange = $event"
            @update:customDateRange="customDateRange = $event"
            @update:condition="condition = $event"
          />
        </template>

        <!-- Include checkboxes -->
        <IncludeCheckboxes
          v-if="exportMode !== 'selected'"
          :include="include"
          @update:include="include = $event"
        />

        <!-- Export What -->
        <ExportWhat
          v-model="exportType"
          :is-selected-mode="exportMode === 'selected'"
        />

        <!-- Columns -->
        <ExportColumns @selected-columns="selectedColumns = $event" />
      </div>

      <!-- Buttons -->
      <div class="button-group">
        <BaseButton
          text
          outlined
          light
          width="83"
          height="44"
          :border-radius="'8px'"
          class="cancel-btn"
          @click="$emit('close')"
        >
          {{ $t('exportBookings.buttons.cancel') }}
        </BaseButton>
        <BaseButton
          width="77"
          height="44"
          :border-radius="'8px'"
          color="primary"
          class="export-btn"
          :loading="isExporting"
          :disabled="isExporting"
          @click="getExportData"
        >
          {{ $t('exportBookings.buttons.export') }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import '@/styles/_colors.scss';
@import '@/styles/bookingList/booking-export.scss';
</style>
